import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import TuneIcon from '@mui/icons-material/Tune';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { useColors } from '../../theme/ColorModeContext';

type Ability = 'copy' | 'drop' | 'store' | 'key';

const abilityInfo: { id: Ability; label: string; blurb: string }[] = [
    { id: 'copy', label: 'copy', blurb: 'Value can be duplicated.' },
    { id: 'drop', label: 'drop', blurb: 'Value can be discarded.' },
    { id: 'store', label: 'store', blurb: 'Value can live inside other structs and storage.' },
    { id: 'key', label: 'key', blurb: 'Value can be a first-class Sui object with a UID.' },
];

interface AbilityOp {
    title: string;
    code: string;
    requires: Ability[];
    explainAllowed: string;
    explainDenied: string;
}

const operations: AbilityOp[] = [
    {
        title: 'Duplicate the value',
        code: 'let b = copy a;',
        requires: ['copy'],
        explainAllowed: 'The value has copy, so a duplicate is legal.',
        explainDenied: 'Without copy this is a resource: it can be moved, never cloned. This is why Sui coins cannot be forged.',
    },
    {
        title: 'Let it go out of scope',
        code: '} // a is never used again',
        requires: ['drop'],
        explainAllowed: 'The value has drop, so the compiler can discard it silently.',
        explainDenied: 'Without drop the value must be explicitly consumed, transferred, or destroyed. Value cannot vanish by accident.',
    },
    {
        title: 'Store it inside another struct',
        code: 'struct Wallet { item: T }',
        requires: ['store'],
        explainAllowed: 'The value has store, so it can be embedded and persisted in other objects.',
        explainDenied: 'Without store the value cannot be wrapped inside another struct or placed in a dynamic field.',
    },
    {
        title: 'Make it a top-level Sui object',
        code: 'transfer::transfer(a, recipient);',
        requires: ['key'],
        explainAllowed: 'The value has key, so it owns a UID and can be transferred, shared, or frozen.',
        explainDenied: 'Without key the value is not a Sui object: it cannot be owned, transferred, or shared on its own.',
    },
    {
        title: 'Hold it as a transferable asset across modules',
        code: 'public fun take(a: T) { /* store + move */ }',
        requires: ['key', 'store'],
        explainAllowed: 'With both key and store the object is a fully transferable Sui asset, like Coin<T>.',
        explainDenied: 'A portable Sui asset needs both key (it is an object) and store (it can be held by others).',
    },
];

const pillars = [
    {
        icon: <ViewInArIcon />,
        title: 'Everything is an object',
        body: 'Sui state is a set of typed objects, each with a unique ID. Transactions take objects as inputs and return objects instead of editing a global account map.',
    },
    {
        icon: <LockIcon />,
        title: 'Resources cannot be copied or lost',
        body: 'Move tracks abilities on every type. A value without copy or drop must be explicitly moved and consumed, so coins cannot be duplicated or silently destroyed.',
    },
    {
        icon: <AltRouteIcon />,
        title: 'Ownership decides parallelism',
        body: 'Objects are owned, shared, or immutable. Owned-object transactions need no global ordering, so Sui can execute them in parallel.',
    },
];

const flow = [
    {
        title: 'Pick objects as inputs',
        body: 'A programmable transaction names the exact objects it reads or mutates, plus a gas coin to pay for execution.',
    },
    {
        title: 'Move runs with abilities checked',
        body: 'The Move VM enforces ability and ownership rules. Resources are moved, never copied, and nothing valuable is dropped by accident.',
    },
    {
        title: 'Validators agree on effects',
        body: 'Owned-object transactions take a fast path; transactions touching shared objects go through consensus for a total order.',
    },
    {
        title: 'Objects are written back',
        body: 'New and changed objects, each with a fresh version, become the inputs available to future transactions.',
    },
];

const takeaways = [
    'Sui stores typed objects with stable IDs and versions, not balances in a global account map.',
    'Move abilities (copy, drop, store, key) decide what a value can do; resources are moved, not copied.',
    'key makes a struct a first-class Sui object; store lets it be held inside others or in dynamic fields.',
    'Ownership (owned, shared, immutable) drives whether a transaction can skip consensus and run in parallel.',
    'Capabilities are objects you must hold to call privileged functions, replacing ambient admin checks.',
    'Most Sui Move bugs come from missing capability checks, trusted owner fields, drifting accounting, and unbounded or squatted object collections.',
];

const AbilitiesDemo = () => {
    const colors = useColors();
    // A typical Sui asset starts as key + store, like Coin<T>.
    const [enabled, setEnabled] = useState<Record<Ability, boolean>>({
        copy: false,
        drop: false,
        store: true,
        key: true,
    });

    const toggle = (id: Ability) => setEnabled((current) => ({ ...current, [id]: !current[id] }));

    const setPreset = (preset: Record<Ability, boolean>) => setEnabled(preset);

    const isResource = !enabled.copy && !enabled.drop;

    const evaluated = useMemo(() => (
        operations.map((op) => ({
            op,
            allowed: op.requires.every((ability) => enabled[ability]),
        }))
    ), [enabled]);

    return (
        <GlowCard glowOnHover={false} gradientBorder sx={{ mb: '24px' }}>
            <Box sx={{ p: { xs: '22px', md: '30px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 1.5 }}>
                    <Box sx={{ color: colors.accent.cyan, '& svg': { fontSize: 32 } }}>
                        <TuneIcon />
                    </Box>
                    <Typography variant="h4" sx={{
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                        color: colors.text.primary,
                    }}>
                        Interactive Move abilities
                    </Typography>
                </Box>
                <Typography sx={{ color: colors.text.secondary, lineHeight: 1.75, mb: 3 }}>
                    In Move, a struct declares <code style={{ fontFamily: '"Share Tech Mono", monospace' }}>has copy, drop, store, key</code>. Those four abilities decide what the value is allowed to do. Toggle them and watch which operations the compiler would accept or reject. A type with neither copy nor drop is a strict resource: the backbone of safe digital assets.
                </Typography>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '0.85fr 1.15fr' },
                    gap: '18px',
                }}>
                    <Box sx={{
                        p: '18px',
                        borderRadius: '8px',
                        backgroundColor: colors.bg.tertiary,
                        border: `1px solid ${colors.border.subtle}`,
                    }}>
                        <Typography sx={{ color: colors.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                            struct Asset has ...
                        </Typography>
                        <Box sx={{ display: 'grid', gap: '10px', mb: 2 }}>
                            {abilityInfo.map((ability) => {
                                const on = enabled[ability.id];
                                return (
                                    <Box
                                        key={ability.id}
                                        onClick={() => toggle(ability.id)}
                                        sx={{
                                            cursor: 'pointer',
                                            p: '12px 14px',
                                            borderRadius: '8px',
                                            backgroundColor: on ? colors.accent.cyanGlow : colors.bg.secondary,
                                            border: `1px solid ${on ? colors.border.accent : colors.border.subtle}`,
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                                            <Typography sx={{
                                                color: on ? colors.accent.cyan : colors.text.secondary,
                                                fontFamily: '"Share Tech Mono", monospace',
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                            }}>
                                                {ability.label}
                                            </Typography>
                                            <Typography sx={{
                                                color: on ? colors.accent.cyan : colors.text.muted,
                                                fontSize: '0.7rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.08em',
                                            }}>
                                                {on ? 'on' : 'off'}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem', mt: 0.5 }}>
                                            {ability.blurb}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setPreset({ copy: false, drop: false, store: true, key: true })}
                                sx={{ borderColor: colors.border.default, color: colors.text.secondary, fontFamily: '"Orbitron", sans-serif' }}
                            >
                                Sui asset
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setPreset({ copy: false, drop: false, store: false, key: false })}
                                sx={{ borderColor: colors.border.default, color: colors.text.secondary, fontFamily: '"Orbitron", sans-serif' }}
                            >
                                Pure resource
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setPreset({ copy: true, drop: true, store: true, key: false })}
                                sx={{ borderColor: colors.border.default, color: colors.text.secondary, fontFamily: '"Orbitron", sans-serif' }}
                            >
                                Plain data
                            </Button>
                        </Box>
                        <Box sx={{
                            mt: 2,
                            p: '12px 14px',
                            borderRadius: '8px',
                            backgroundColor: colors.bg.secondary,
                            border: `1px solid ${isResource ? colors.accent.green : colors.border.subtle}`,
                        }}>
                            <Typography sx={{ color: isResource ? colors.accent.green : colors.text.secondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
                                {isResource
                                    ? 'No copy and no drop: this is a resource. It can only be moved and must be explicitly consumed.'
                                    : 'With copy or drop this type behaves more like ordinary data than a protected asset.'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gap: '10px' }}>
                        {evaluated.map(({ op, allowed }) => (
                            <Box key={op.title} sx={{
                                p: '14px',
                                borderRadius: '8px',
                                backgroundColor: colors.bg.tertiary,
                                border: `1px solid ${allowed ? colors.accent.green : colors.accent.red}`,
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', mb: 0.5 }}>
                                    <Typography sx={{ color: colors.text.primary, fontWeight: 700, fontSize: '0.95rem' }}>
                                        {op.title}
                                    </Typography>
                                    <Typography sx={{
                                        color: allowed ? colors.accent.green : colors.accent.red,
                                        fontFamily: '"Orbitron", sans-serif',
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {allowed ? 'compiles' : 'rejected'}
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    color: colors.text.secondary,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    mb: 0.75,
                                    wordBreak: 'break-all',
                                }}>
                                    {op.code}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: 0.75 }}>
                                    {op.requires.map((ability) => (
                                        <Chip
                                            key={ability}
                                            label={ability}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.68rem',
                                                fontFamily: '"Share Tech Mono", monospace',
                                                color: enabled[ability] ? colors.accent.green : colors.accent.red,
                                                borderColor: enabled[ability] ? colors.accent.green : colors.accent.red,
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Typography sx={{ color: colors.text.muted, fontSize: '0.83rem', lineHeight: 1.6 }}>
                                    {allowed ? op.explainAllowed : op.explainDenied}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </GlowCard>
    );
};

const SuiExplainer = () => {
    const colors = useColors();

    const sectionTitleSx = {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 700,
        letterSpacing: '0.01em',
        color: colors.text.primary,
        mb: 1.5,
    };

    const bodySx = {
        color: colors.text.secondary,
        lineHeight: 1.75,
        fontSize: '1rem',
    };

    return (
        <PageContainer maxWidth="lg">
            <CyberpunkTitle
                segments={[
                    { label: 'Sui', to: '/academy/sui-move' },
                    { label: 'Sui' },
                ]}
                mb="24px"
            />

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1.05fr 0.95fr' },
                gap: '24px',
                alignItems: 'stretch',
                mb: '24px',
            }}>
                <GlowCard glowOnHover={false} gradientBorder sx={{ minHeight: '100%' }}>
                    <Box sx={{ p: { xs: '22px', md: '32px' } }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 3 }}>
                            {['Move', 'Object-Centric', 'Capabilities'].map((label) => (
                                <Chip
                                    key={label}
                                    label={label}
                                    size="small"
                                    sx={{
                                        color: colors.accent.cyan,
                                        borderColor: colors.border.accent,
                                        backgroundColor: colors.accent.cyanGlow,
                                        fontFamily: '"Orbitron", sans-serif',
                                    }}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                        <Typography variant="h2" sx={{
                            fontFamily: '"Orbitron", sans-serif',
                            fontSize: { xs: '2rem', md: '3rem' },
                            lineHeight: 1.08,
                            fontWeight: 800,
                            letterSpacing: 0,
                            mb: 2,
                        }}>
                            Sui is a network of typed objects you own and move.
                        </Typography>
                        <Typography sx={{
                            color: colors.accent.green,
                            fontFamily: '"Orbitron", sans-serif',
                            fontSize: { xs: '1rem', md: '1.08rem' },
                            fontWeight: 700,
                            mb: 2,
                        }}>
                            In one sentence: Sui represents state as typed Move objects, and transactions move those objects under strict ownership and ability rules.
                        </Typography>
                        <Typography sx={{ ...bodySx, fontSize: { xs: '1rem', md: '1.08rem' }, mb: 3 }}>
                            The model is object-first. Instead of a global balance map, Sui holds typed objects with unique IDs. Contracts are Move modules, and Move's ability system decides what each value can do. Ownership, capabilities, and the no-copy rule for resources are what keep assets safe.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/academy/move-foundation"
                            variant="outlined"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                borderColor: colors.border.accent,
                                color: colors.accent.cyan,
                                fontFamily: '"Orbitron", sans-serif',
                                '&:hover': {
                                    borderColor: colors.accent.cyan,
                                    backgroundColor: colors.accent.cyanGlow,
                                },
                            }}
                        >
                            Continue to Foundation
                        </Button>
                    </Box>
                </GlowCard>

                <GlowCard glowOnHover={false} sx={{ minHeight: '100%' }}>
                    <Box sx={{ position: 'relative', height: '100%', minHeight: { xs: '320px', md: '420px' }, overflow: 'hidden' }}>
                        <Box
                            component="img"
                            src="/images/academy/sui-network.png"
                            alt="Sui object-centric model and Move illustration"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(180deg, rgba(10,14,23,0.02) 0%, rgba(10,14,23,0.68) 100%)',
                        }} />
                        <Box sx={{
                            position: 'absolute',
                            left: 24,
                            right: 24,
                            bottom: 24,
                            display: 'grid',
                            gap: '10px',
                        }}>
                            {[
                                ['Objects', 'typed state with unique IDs'],
                                ['Move', 'resource-safe contract language'],
                                ['Ownership', 'owned, shared, or immutable'],
                            ].map(([label, detail]) => (
                                <Box key={label} sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    p: '10px 12px',
                                    border: `1px solid ${colors.border.default}`,
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(10, 14, 23, 0.72)',
                                    backdropFilter: 'blur(10px)',
                                }}>
                                    <Typography sx={{ color: colors.text.primary, fontWeight: 700, fontSize: '0.86rem' }}>{label}</Typography>
                                    <Typography sx={{ color: colors.text.secondary, fontSize: '0.82rem', textAlign: 'right' }}>{detail}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </GlowCard>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: '18px',
                mb: '24px',
            }}>
                {pillars.map((pillar) => (
                    <GlowCard key={pillar.title} glowOnHover={false} accentTop>
                        <Box sx={{ p: '22px' }}>
                            <Box sx={{ color: colors.accent.cyan, mb: 1, '& svg': { fontSize: 30 } }}>
                                {pillar.icon}
                            </Box>
                            <Typography variant="h6" sx={sectionTitleSx}>{pillar.title}</Typography>
                            <Typography sx={{ ...bodySx, fontSize: '0.94rem' }}>{pillar.body}</Typography>
                        </Box>
                    </GlowCard>
                ))}
            </Box>

            <AbilitiesDemo />

            <GlowCard glowOnHover={false} sx={{ mb: '24px' }}>
                <Box sx={{ p: { xs: '22px', md: '30px' } }}>
                    <Typography variant="h4" sx={sectionTitleSx}>The mental model</Typography>
                    <Typography sx={{ ...bodySx, mb: 2 }}>
                        Think of Sui as a warehouse of typed objects rather than a ledger of balances. Every coin, NFT, and piece of protocol state is an object with a unique ID and a version. A transaction takes specific objects as inputs, runs Move code over them, and produces updated objects as outputs.
                    </Typography>
                    <Typography sx={bodySx}>
                        Move's type system is the safety layer. Abilities decide whether a value can be copied, dropped, stored, or made into a Sui object, and ownership decides who can use it and whether the transaction needs consensus. Get those rules wrong and you get the exact bugs the security tracks exploit.
                    </Typography>
                </Box>
            </GlowCard>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '0.95fr 1.05fr' },
                gap: '24px',
                mb: '24px',
            }}>
                <GlowCard glowOnHover={false}>
                    <Box sx={{ p: { xs: '22px', md: '30px' } }}>
                        <Typography variant="h4" sx={sectionTitleSx}>How a Sui transaction moves</Typography>
                        <Box sx={{ display: 'grid', gap: '14px', mt: 2 }}>
                            {flow.map((step, index) => (
                                <Box key={step.title} sx={{ display: 'grid', gridTemplateColumns: '42px 1fr', gap: '14px' }}>
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        display: 'grid',
                                        placeItems: 'center',
                                        color: colors.bg.primary,
                                        background: colors.gradient.primary,
                                        fontWeight: 800,
                                        fontFamily: '"Orbitron", sans-serif',
                                    }}>
                                        {index + 1}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: colors.text.primary, fontWeight: 700, mb: 0.5 }}>{step.title}</Typography>
                                        <Typography sx={{ ...bodySx, fontSize: '0.94rem' }}>{step.body}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </GlowCard>

                <GlowCard glowOnHover={false}>
                    <Box sx={{ p: { xs: '22px', md: '30px' } }}>
                        <Typography variant="h4" sx={sectionTitleSx}>What the object model protects</Typography>
                        <Typography sx={{ ...bodySx, mb: 2 }}>
                            Because resources cannot be copied, a coin object can only ever be moved from one owner to another. Because owned objects are versioned, a transaction that references a stale version is rejected, which removes whole classes of double-spend and replay bugs.
                        </Typography>
                        <Typography sx={{ ...bodySx, mb: 2 }}>
                            The type system does not police your business logic. If a function forgets a capability check, trusts a caller-supplied owner field, or lets a shared object grow without bound, Move will happily compile it. That is where auditing begins.
                        </Typography>
                        <Divider sx={{ borderColor: colors.border.subtle, my: 2.5 }} />
                        <Box sx={{ display: 'grid', gap: '12px' }}>
                            {[
                                ['Abilities', 'The compiler enforces copy, drop, store, and key on every type.'],
                                ['Ownership', 'Owned, shared, and immutable objects decide access and execution path.'],
                                ['Your logic', 'Still your responsibility: capabilities, accounting, and bounds are not automatic.'],
                            ].map(([label, detail]) => (
                                <Box key={label} sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' },
                                    gap: '10px',
                                    p: '14px',
                                    borderRadius: '8px',
                                    backgroundColor: colors.bg.tertiary,
                                    border: `1px solid ${colors.border.subtle}`,
                                }}>
                                    <Typography sx={{ color: colors.text.primary, fontWeight: 700 }}>{label}</Typography>
                                    <Typography sx={{ color: colors.text.secondary, lineHeight: 1.6 }}>{detail}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </GlowCard>
            </Box>

            <GlowCard glowOnHover={false} gradientBorder>
                <Box sx={{ p: { xs: '22px', md: '30px' } }}>
                    <Typography variant="h4" sx={sectionTitleSx}>The pieces you should recognize before the Sui challenges</Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                        gap: '14px',
                        mt: 2,
                    }}>
                        {[
                            [<ViewInArIcon />, 'Objects', 'Typed state with a UID and a version, owned or shared.'],
                            [<TuneIcon />, 'Abilities', 'copy, drop, store, and key decide what a value can do.'],
                            [<AltRouteIcon />, 'Ownership', 'Owned, shared, and immutable objects drive execution.'],
                            [<VpnKeyIcon />, 'Capabilities', 'Objects you must hold to call privileged functions.'],
                        ].map(([icon, title, body]) => (
                            <Box key={title as string} sx={{
                                p: '16px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.border.subtle}`,
                                backgroundColor: colors.bg.tertiary,
                            }}>
                                <Box sx={{ color: colors.accent.green, mb: 1, '& svg': { fontSize: 28 } }}>{icon}</Box>
                                <Typography sx={{ color: colors.text.primary, fontWeight: 800, mb: 0.75 }}>{title}</Typography>
                                <Typography sx={{ ...bodySx, fontSize: '0.9rem' }}>{body}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{ ...bodySx, mt: 3 }}>
                        The exercises start by rebuilding Move itself (values, abilities, ownership, modules, and tests), then map those rules onto Sui objects before switching sides to exploit intentionally vulnerable models. Keep the abilities demo in mind: Sui security depends on exact ownership, exact capabilities, and resources that can only be moved.
                    </Typography>
                    <Divider sx={{ borderColor: colors.border.subtle, my: 2.5 }} />
                    <Typography sx={{
                        color: colors.text.primary,
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 800,
                        mb: 1.5,
                    }}>
                        What to remember
                    </Typography>
                    <Box component="ul" sx={{
                        color: colors.text.secondary,
                        lineHeight: 1.75,
                        m: 0,
                        pl: '20px',
                    }}>
                        {takeaways.map((takeaway) => (
                            <Box component="li" key={takeaway} sx={{ mb: 0.75 }}>
                                {takeaway}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </GlowCard>
        </PageContainer>
    );
};

export default SuiExplainer;
